<template>
  <div
    :class="[
      `formulate-input-element formulate-input-element--${context.type} input-${size}`,
      { 'is-disabled': isDisabled },
    ]"
    :data-type="context.type"
  >
    <!-- Desktop: Use original date-picker -->
    <mc-date-picker
      v-if="!isMobile"
      v-bind="context.attributes"
      v-model="context.model"
      :disabled="isDisabled"
      :value-type="context.attributes?.valueType || context.attributes?.['value-type'] || 'format'"
      :placeholder="$t(context.attributes?.placeholder) || $t('global.action.selectDate')"
      :type="datepickerType"
      ref="periodePicker"
      @blur="context.blurHandler()"
      @pick="pick"
      @close="close"
      :lang="datePickerLang"
      :clearable="clearable"
      :draft="isDraftMode"
    >
      <template #footer v-if="hasCustomFooter">
        <div v-if="availableRangeText" class="mc-datepicker-footer__available-range">
          <span class="me-2">Data tersedia:</span>
          <strong>{{ availableRangeText }}</strong>
        </div>
        <div v-if="context.attributes.cancel" class="mc-datepicker-footer__cancel-wrap">
          <b-button
            class="mc-datepicker-cancel-btn"
            variant="outline-primary"
            @click="closeCalendar()"
          >
            {{ $t('global.action.cancel') }}
          </b-button>
        </div>
      </template>
      <template #icon-calendar>
        <i :class="icon"></i>
      </template>
    </mc-date-picker>

    <!-- Mobile: Use custom modal picker -->
    <template v-else>
      <div
        :class="['mc-mobile-datepicker-fake-input', { 'is-disabled': isDisabled }]"
        @click="openMobilePicker"
      >
        <input
          type="text"
          readonly
          class="mx-input"
          :placeholder="$t(context.attributes?.placeholder) || $t('global.action.selectDate')"
          :value="mobileDisplayValue"
          :disabled="context.attributes?.disabled"
        />
        <span class="mx-input-append">
          <i :class="icon"></i>
        </span>
      </div>

      <McMobileDateTimePicker
        v-if="showMobilePicker"
        :value="context.model"
        :disabled-date="context.attributes?.['disabled-date'] || context.attributes?.disabledDate"
        :show-time="datepickerType === 'datetime'"
        :range="context.attributes?.range === true || context.attributes?.range === 'true'"
        :available-range-text="availableRangeText"
        @input="onMobileInput"
        @close="showMobilePicker = false"
      />
    </template>
  </div>
</template>

<script>
import datePickerLang from '@/helpers/date-picker-lang'
import McMobileDateTimePicker from '@/components/McMobileDateTimePicker.vue'
import McDatePicker from '@/components/McDatePicker'
import moment from 'moment'

export default {
  components: {
    McMobileDateTimePicker,
    McDatePicker,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    datepickerType: {
      type: String,
      default: 'date',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      default: 'icon-outline_calendar',
    },
  },

  data() {
    return {
      showMobilePicker: false,
      isMobile: false,
    }
  },

  computed: {
    datePickerLang() {
      return datePickerLang()
    },
    isDisabled() {
      return (
        this.context.attributes?.disabled === true ||
        this.context.attributes?.disabled === 'true' ||
        this.context.attributes?.disabled === ''
      )
    },
    availableRangeText() {
      return (
        this.context.attributes?.availableRangeText ||
        this.context.attributes?.['available-range-text'] ||
        ''
      )
    },
    hasCustomFooter() {
      return Boolean(this.context.attributes?.cancel || this.availableRangeText)
    },
    isDraftMode() {
      const range = this.context.attributes?.range
      const confirm = this.context.attributes?.confirm
      const draft = this.context.attributes?.draft
      return (
        (draft === true || draft === 'true') &&
        (range === true || range === 'true') &&
        (confirm === true || confirm === 'true')
      )
    },
    mobileDisplayValue() {
      if (!this.context.model) return ''
      
      const isRange = this.context.attributes?.range === true || this.context.attributes?.range === 'true'
      const format = this.context.attributes?.format || (this.datepickerType === 'datetime' ? 'DD MMM YYYY HH:mm' : 'DD MMM YYYY')
      const valueType = this.context.attributes?.valueType || this.context.attributes?.['value-type'] || 'format'
      
      const formatDate = (val) => {
        let date
        if (valueType === 'format') {
          const inputFormat = this.context.attributes?.format || 'YYYY-MM-DD HH:mm:ss'
          date = moment(val, inputFormat)
        } else {
          date = moment(val, valueType)
        }
        return date.isValid() ? date.format(format) : val
      }

      if (isRange && Array.isArray(this.context.model)) {
        return this.context.model.map(v => formatDate(v)).join(' - ')
      }

      return formatDate(this.context.model)
    }
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 890
    },
    openMobilePicker() {
      if (this.isDisabled) return
      this.showMobilePicker = true
    },
    onMobileInput(value) {
      const valueType =
        this.context.attributes?.valueType || this.context.attributes?.['value-type'] || 'format'
      const format = this.context.attributes?.format || 'YYYY-MM-DD HH:mm:ss'
      
      const formatValue = (val) => {
        if (valueType === 'format') {
          return moment(val).format(format)
        } else {
          return moment(val).format(valueType)
        }
      }

      if (Array.isArray(value)) {
        this.context.model = value.map(v => formatValue(v))
      } else {
        this.context.model = formatValue(value)
      }
      
      this.showMobilePicker = false
    },
    closeCalendar() {
      if (this.$refs.periodePicker) {
        this.$refs.periodePicker.cancel()
      }
    },
    pick(date) {
      this.$listeners && this.$listeners.pick && this.$emit('pick', date)
    },
    close(date) {
      this.$listeners && this.$listeners.close && this.$emit('close', date)
    },
  },
}
</script>

<style scoped lang="scss">
.mc-mobile-datepicker-fake-input {
  width: 100%;
  position: relative;
  cursor: pointer;

  .mx-input {
    font-size: 12px;
    cursor: pointer;
  }

  .mx-input-append {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    color: #99a1b1;
    pointer-events: none;
  }

  &.is-disabled {
    cursor: not-allowed;

    .mx-input {
      color: #99a1b1 !important;
      background-color: #e7ecf4 !important;
      border-color: #d9e2ef !important;
      cursor: not-allowed !important;
    }

    .mx-input::placeholder {
      color: #99a1b1 !important;
    }

    .mx-input-append {
      color: #b8c2d1 !important;
    }
  }
}

.mc-datepicker-footer__available-range {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  color: #344264;
  padding: 10px 12px;
  border-top: 1px solid #d9e2ef;
  border-bottom: 1px solid #e2e8f3;
  margin: 6px -8px 8px;
}

.mc-datepicker-footer__cancel-wrap {
  display: inline-flex;
  margin-left: auto;
  margin-right: 8px;
}

.mc-datepicker-cancel-btn {
  min-height: 36px;
  padding: 0 16px;
  position: relative;
  top: -1px;
}

:deep(.mx-datepicker-footer) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 8px 10px;
}

:deep(.mx-datepicker-footer .mx-btn) {
  margin: 0;
}

:deep(.mx-datepicker input:disabled),
:deep(.mx-input:disabled),
:deep(.mx-datepicker.disabled .mx-input),
:deep(.mx-datepicker.disabled .mx-input-wrapper) {
  color: #99a1b1 !important;
  background-color: #e7ecf4 !important;
  border-color: #d9e2ef !important;
  cursor: not-allowed !important;
}

:deep(.mx-datepicker input:disabled::placeholder),
:deep(.mx-input:disabled::placeholder),
:deep(.mx-datepicker.disabled .mx-input::placeholder) {
  color: #99a1b1 !important;
}

:deep(.mx-datepicker input:disabled ~ .mx-input-append),
:deep(.mx-datepicker .mx-input-wrapper input:disabled ~ .mx-input-append),
:deep(.mx-datepicker.disabled .mx-icon-calendar) {
  color: #b8c2d1 !important;
  cursor: not-allowed !important;
}
</style>
